import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import Modal, { Field, TextInput, PrimaryButton, SecondaryButton } from "@/components/FormKit";
import { toast } from "sonner";
import { fmtCurrency, fmtDate } from "@/lib/format";

const initial = {
  external_customer_id: "", invoice_no: "", amount: "",
  issue_date: "", file_url: "", description: "",
};

export default function Invoices() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);

  const load = async () => setItems((await api.get("/company/invoices")).data);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/company/invoices", { ...form, amount: Number(form.amount) });
      toast.success("Invoice imported");
      setOpen(false);
      setForm(initial);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-8" data-testid="company-invoices-page">
      <PageHeader
        eyebrow="External"
        title="Invoice records"
        description="Invoices originated in your ERP/accounting system. External customer IDs auto-map to internal customers."
        actions={<PrimaryButton onClick={() => setOpen(true)} testId="new-invoice-button">+ Import Invoice</PrimaryButton>}
      />

      <DataTable
        testId="invoices-table"
        columns={[
          { key: "invoice_no", label: "Invoice #", render: (r) => <span className="font-mono font-bold">{r.invoice_no}</span> },
          { key: "customer", label: "Customer", render: (r) => r.customer?.name || <span className="text-stone-400 text-xs">Unmapped</span> },
          { key: "external_customer_id", label: "External ID", render: (r) => <span className="font-mono text-xs">{r.external_customer_id}</span> },
          { key: "amount", label: "Amount", render: (r) => <span className="font-mono">{fmtCurrency(r.amount)}</span> },
          { key: "issue_date", label: "Issued", render: (r) => fmtDate(r.issue_date) },
          {
            key: "file_url",
            label: "File",
            render: (r) =>
              r.file_url ? (
                <a href={r.file_url} target="_blank" rel="noreferrer" className="text-brand-600 underline text-xs">
                  download
                </a>
              ) : "—",
          },
        ]}
        rows={items}
        empty="No invoices imported yet."
      />

      <Modal open={open} onClose={() => setOpen(false)} title="Import invoice from external system">
        <form onSubmit={submit}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="External customer ID"><TextInput testId="form-inv-ext" required value={form.external_customer_id} onChange={(e) => setForm({ ...form, external_customer_id: e.target.value })} /></Field>
            <Field label="Invoice number"><TextInput testId="form-inv-no" required value={form.invoice_no} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} /></Field>
            <Field label="Amount"><TextInput testId="form-inv-amount" type="number" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Field>
            <Field label="Issue date"><TextInput testId="form-inv-date" type="date" required value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} /></Field>
          </div>
          <Field label="File URL (PDF link)"><TextInput testId="form-inv-file" value={form.file_url} onChange={(e) => setForm({ ...form, file_url: e.target.value })} /></Field>
          <Field label="Description"><TextInput testId="form-inv-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <div className="flex justify-end gap-2 pt-2">
            <SecondaryButton type="button" onClick={() => setOpen(false)}>Cancel</SecondaryButton>
            <PrimaryButton type="submit" testId="submit-invoice">Import</PrimaryButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
